/**
 * Print service: initiatePrint, printImmediately, processPrintingNonBlocking.
 * Orthogonal to upload pipeline; pages call directly.
 */

import { PrintUtils, buildPrintOptions } from '@renderer/utils/printUtils'
import type { PrintSettings } from '@renderer/stores/printStore'
import { logPrintEvent } from '@renderer/utils/printEventLogger'
import { getLastRoutingMatch } from '@renderer/stores/printStore'
import { installPrintQueueListener, shouldWatchPrintQueue } from '@renderer/utils/printQueueOutcome'

export type PrintEventType = 'session' | 'user_reprint' | 'operator_reprint' | 'test_print'

/**
 * Ties a print event back to the session it belongs to. Without it a failed
 * print is only visible as a line in the booth's activity log, so support has to
 * hand-trace a reported session id to find out what happened.
 */
export interface PrintEventContext {
  sessionId?: string
  projectId?: string
}

export async function initiatePrint(
  stripBlob: Blob,
  printerName: string,
  copies: number,
  settings: PrintSettings,
  isOnline: boolean = true,
  duplicateCheckId?: string,
  eventType: PrintEventType = 'session',
  context?: PrintEventContext
): Promise<void> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = async (): Promise<void> => {
      try {
        const base64data = reader.result as string
        // Ask the shell to watch the Windows queue for this job (kill switch:
        // the print_job_watch flag). See utils/printQueueOutcome.
        const watch = shouldWatchPrintQueue()
        if (watch) installPrintQueueListener()
        const printResult = await PrintUtils.sendToPrinter(
          base64data,
          buildPrintOptions(printerName, settings, {
            copies,
            duplicateCheckId,
            watchQueue: watch ? { sessionId: context?.sessionId, projectId: context?.projectId } : null
          })
        )

        // A remote job ignores the caller's (local-slot) printer name; log the
        // printer it actually targeted, or the event log claims a DS-RX1 print
        // while the job went to another machine entirely.
        const printedOn = printResult.printedOn
        const loggedPrinterName = printedOn?.printerName ?? printerName
        const remoteFields = printedOn
          ? {
              printerTarget: 'remote' as const,
              remotePrinterMachineId: printedOn.machineIdentifier
            }
          : { printerTarget: 'default' as const }

        if (printResult.success) {
          void logPrintEvent({
            eventType,
            copies,
            printerName: loggedPrinterName,
            printerType: printResult.printerType,
            ...remoteFields,
            routingMatch: getLastRoutingMatch(),
            success: true,
            ...context
          })
          resolve()
        } else {
          const errorMsg =
            printResult.failureReason || printResult.error || 'Unknown error'
          void logPrintEvent({
            eventType,
            copies,
            printerName: loggedPrinterName,
            printerType: printResult.printerType,
            ...remoteFields,
            success: false,
            error: errorMsg,
            ...context
          })
          const isNetworkError =
            !isOnline &&
            (printResult.error?.includes('network') ||
              printResult.error?.includes('fetch') ||
              printResult.failureReason?.includes('network'))
          if (!isNetworkError || isOnline) {
            reject(new Error(errorMsg))
          } else {
            resolve()
          }
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : 'Unknown error'
        void logPrintEvent({
          eventType,
          copies,
          printerName,
          success: false,
          error: errMsg,
          ...context
        })
        reject(error instanceof Error ? error : new Error('Unknown error'))
      }
    }
    reader.onerror = (): void => {
      const errorMsg = 'Failed to read file for printing'
      void logPrintEvent({
        eventType,
        copies,
        printerName,
        success: false,
        error: errorMsg,
        ...context
      })
      reject(new Error(errorMsg))
    }
    reader.readAsDataURL(stripBlob)
  })
}

export function printImmediately(
  stripBlob: Blob,
  printerName: string,
  copies: number,
  settings: PrintSettings,
  onSuccess?: () => void,
  onError?: (error: string) => void,
  isOnline: boolean = true,
  eventType: PrintEventType = 'session'
): void {
  const reader = new FileReader()
  reader.onloadend = async (): Promise<void> => {
    try {
      const base64data = reader.result as string
      const printResult = await PrintUtils.sendToPrinter(
        base64data,
        buildPrintOptions(printerName, settings, { copies })
      )
      // Same remote-target correction as initiatePrint above.
      const printedOn = printResult.printedOn
      const loggedPrinterName = printedOn?.printerName ?? printerName
      const remoteFields = printedOn
        ? {
            printerTarget: 'remote' as const,
            remotePrinterMachineId: printedOn.machineIdentifier
          }
        : { printerTarget: 'default' as const }
      if (printResult.success) {
        void logPrintEvent({
          eventType,
          copies,
          printerName: loggedPrinterName,
          printerType: printResult.printerType,
          ...remoteFields,
          success: true
        })
        onSuccess?.()
      } else {
        const errorMsg = printResult.failureReason || printResult.error || 'Unknown error'
        void logPrintEvent({
          eventType,
          copies,
          printerName: loggedPrinterName,
          printerType: printResult.printerType,
          ...remoteFields,
          success: false,
          error: errorMsg
        })
        const isNetworkError =
          !isOnline &&
          (printResult.error?.includes('network') ||
            printResult.error?.includes('fetch') ||
            printResult.failureReason?.includes('network'))
        if (!isNetworkError || isOnline) onError?.(errorMsg)
      }
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : 'Unknown error'
      void logPrintEvent({ eventType, copies, printerName, success: false, error: errMsg })
      onError?.(errMsg)
    }
  }
  reader.onerror = (): void => {
    const errorMsg = 'Failed to read file for printing'
    void logPrintEvent({ eventType, copies, printerName, success: false, error: errorMsg })
    onError?.(errorMsg)
  }
  reader.readAsDataURL(stripBlob)
}

export async function processPrintingNonBlocking(
  stripBlob: Blob,
  printerName: string,
  copies: number,
  settings: PrintSettings,
  onSuccess?: () => void,
  onError?: (error: string) => void,
  isOnline: boolean = true
): Promise<void> {
  printImmediately(stripBlob, printerName, copies, settings, onSuccess, onError, isOnline)
}
